import React, { useState, useRef, useEffect } from "react";
import { Delete, Check } from "lucide-react";
import { login } from "../lib/api";
import { useAudio } from "./AudioProvider";
import { SpeakerButton } from "./SpeakerButton";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
const HELP_TEXT = "Welcome to A V Nexus. Please tap your secret PIN and press the green button.";
const WRONG_TEXT = "Wrong number. Please try again.";

export default function PinLogin({ onLogin, onRegister, onBack }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const padRef = useRef(null);
  const { speak } = useAudio();

  useEffect(() => {
    const t = setTimeout(() => speak(HELP_TEXT, 1.0, "login-help"), 400);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  const press = (d) => {
    if (busy) return;
    setError(false);
    setPin((p) => (p.length < 8 ? p + d : p));
  };
  const back = () => { setError(false); setPin((p) => p.slice(0, -1)); };

  const submit = async () => {
    if (!pin || busy) return;
    setBusy(true);
    try {
      const res = await login(pin);
      if (res.ok) {
        onLogin(res);
      } else {
        setError(true);
        speak(WRONG_TEXT, 1.0, "wrong");
        setPin("");
      }
    } catch (e) {
      setError(true);
      speak(WRONG_TEXT, 1.0, "wrong");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[#FAFAF7]">
      <div className="w-full max-w-sm flex flex-col items-center">
        {onBack && (
          <button onClick={onBack} data-testid="pin-back" className="self-start mb-3 text-[#50564F] font-bold flex items-center gap-1 hover:text-[#1A1C19]">
            ← A V Nexus
          </button>
        )}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-14 h-14 rounded-2xl bg-[#FFD600] border-3 border-[#1A1C19] flex items-center justify-center yb-tactile" style={{ boxShadow: "0 4px 0 0 #1A1C19" }}>
            <span className="text-2xl font-extrabold">A</span>
          </div>
          <h1 className="yb-heading text-4xl font-extrabold tracking-tight">A V Nexus</h1>
        </div>
        <p className="text-[#50564F] text-lg mb-6">Enter your PIN</p>

        <div
          data-testid="pin-display"
          ref={padRef}
          className={`w-full mb-6 min-h-[80px] rounded-2xl bg-[#FEFCE8] border-3 border-[#1A1C19] flex items-center justify-center gap-3 px-4 ${error ? "yb-shake border-[#DC2626]" : ""}`}
          style={{ boxShadow: error ? "0 4px 0 0 #DC2626" : "0 4px 0 0 #1A1C19" }}
        >
          {pin.length === 0 ? (
            <span className="text-[#50564F] text-3xl">— — —</span>
          ) : (
            pin.split("").map((d, i) => (
              <span key={i} className="text-5xl font-extrabold text-[#1A1C19]">{d}</span>
            ))
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 w-full">
          {KEYS.map((k) => (
            <button
              key={k}
              data-testid={`pin-keypad-digit-${k}`}
              onClick={() => press(k)}
              className="yb-tactile yb-focus h-20 bg-white text-4xl font-extrabold hover:bg-[#FFD600]"
            >
              {k}
            </button>
          ))}
          <button
            data-testid="pin-keypad-back"
            onClick={back}
            className="yb-tactile yb-focus h-20 bg-white flex items-center justify-center hover:bg-[#FEE2E2]"
            aria-label="Delete"
          >
            <Delete className="w-8 h-8" />
          </button>
          <button
            data-testid="pin-keypad-digit-0"
            onClick={() => press("0")}
            className="yb-tactile yb-focus h-20 bg-white text-4xl font-extrabold hover:bg-[#FFD600]"
          >
            0
          </button>
          <button
            data-testid="pin-keypad-submit"
            onClick={submit}
            disabled={busy}
            className="yb-tactile yb-focus h-20 bg-[#15803D] text-white flex items-center justify-center hover:bg-[#166534] disabled:opacity-60"
            aria-label="Enter"
          >
            <Check className="w-10 h-10" strokeWidth={3} />
          </button>
        </div>

        <div className="mt-8">
          <SpeakerButton text={HELP_TEXT} tag="login-help" label="Help" testId="login-help-button" />
        </div>

        <button
          onClick={onRegister}
          data-testid="go-register-button"
          className="yb-tactile yb-focus mt-4 bg-white px-6 py-3 font-bold text-lg hover:bg-[#FEFCE8]"
        >
          New account
        </button>
      </div>
    </div>
  );
}
